<script setup lang="ts">
import RemoteFile from '@/core/resources/RemoteFile'
import Server from '@/core/resources/Server'
import { serverService } from '@/core/services'
import { computed, ref } from 'vue'
import { useStore } from '@/core/store'

const store = useStore()
const children = ref<RemoteFile[]>([])
const props = defineProps<{
  server: Server
  file: RemoteFile
  filterHidden: boolean
  search: string
}>()
const unRolled = ref(false)

const unRoll = () => {
  if (!children.value.length) {
    fetchChildren()
  }
  unRolled.value = !unRolled.value
}

const fetchChildren = async () => {
  children.value = (await serverService.getServerFiles(props.server.id, props.file.path, store.getAccessToken())).filter(
    (file) => file.type === 'dir'
  )
}

const filteredChildren = computed(() => {
  const dotFilter = (file: RemoteFile) => (props.filterHidden ? !file.basename.startsWith('.') : true)

  const searchFilter = (file: RemoteFile) => (props.search ? file.basename.toLowerCase().includes(props.search.toLowerCase()) : true)

  return children.value.filter((file) => dotFilter(file) && searchFilter(file))
})

const emit = defineEmits({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changePath: (path: string) => true
})
</script>

<template>
  <div class="file-list-item__container">
    <div class="file-list-item" @click.stop="emit('changePath', file.path)">
      <div class="file-list-item__left">
        <span class="material-symbols-outlined chevron" :class="{ active: unRolled }" @click.stop="unRoll"> chevron_right </span>

        <span class="material-symbols-outlined" :class="{ hidden: file.basename.startsWith('.') }"> folder </span>

        <div class="file-list-item__details">
          <div>{{ file.basename }}</div>
        </div>
      </div>
    </div>
    <div class="children-list" :class="{ active: unRolled }">
      <RemoteFileTreeItem
        v-for="child in filteredChildren"
        :server="server"
        :key="child.path"
        :file="child"
        :filterHidden="filterHidden"
        :search="search"
        @changePath="emit('changePath', $event)" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.file-list-item__container {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.chevron {
  transition: transform 0.2s;

  &.active {
    transform: rotate(90deg);
  }
}

.children-list {
  display: none;
  flex-direction: column;
  max-height: 0;
  margin-left: 1rem;
  overflow: hidden;
}

.children-list.active {
  display: flex;
  flex-direction: column;
  max-height: 100%;
}

.file-list-item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.2rem;
  padding-right: 1rem;
  justify-content: space-between;

  transition: background-color 0.2s;

  user-select: none;
  cursor: pointer;

  &:hover {
    background-color: var(--theme-panel-secondary);
  }

  &:active {
    background-color: var(--theme-panel-tertiary);
  }
}

.file-list-item__left {
  display: flex;
  align-items: center;
}

.material-symbols-outlined {
  font-size: 1.5rem;
  margin-right: 0.5rem;
  color: #555;

  &.hidden {
    color: var(--theme-panel-tertiary);
  }
}
</style>
